/**
 * **************************************************************
 * Copyright (C) 2015-2020, 2023 VMware, Inc. All rights reserved.
 * **************************************************************
 *
 * @format
 */

/**
 * @fileoverview audioworker.js -- audioWorker
 * Class to wrap audio and video encoding
 * encoderStatus need to be extended to also constains wmksSessionID and param
 * if we want to support seamless window with RTAV.
 */

import RTAV from "./logservice";

export default function MediaEncoder() {
   // private Class
   let LimitedSet,
      // priavte properties:
      workers,
      headers,
      callbacks,
      resourcesCount,
      enabled,
      encodingEnvs,
      encoderStatus,
      messageManager,
      codecPrefs,
      // private functions:
      resetEncoder,
      initInstance,
      arrayCopy,
      setMergedHeader,
      setAudioHeader,
      handleMessage,
      handleAudioMessage,
      handleVideoMessage,
      encode,
      clearWorker,
      clear;

   /**
    * We choose to use an extra bool list instead of the special value in the buffer to mark
    * @private
    */
   LimitedSet = function(capacity) {
      this.buff = new Array(capacity);
      this.occupied = Array.apply(null, Array(capacity)).map(
         Boolean.prototype.valueOf,
         false
      );
      this.insert = function(item) {
         let i;
         for (i = 0; i < this.occupied.length; i++) {
            if (!this.occupied[i]) {
               this.buff[i] = item;
               this.occupied[i] = true;
               return i;
            }
         }
         return -1;
      };
      // single thread, and inner usage, so skip id tests
      this.fetch = function(id) {
         this.occupied[id] = false;
         return this.buff[id];
      };
   };

   resetEncoder = function() {
      headers = {
         audio: null,
         video: null,
         merged: null
      };
      callbacks = {
         video: null,
         audio: null,
         header: null
      };
      /**
       * number used for PV operation avoid taking too much of resources in the event queue,
       * and the values are get by testing on different machines, the bigger the number
       * the less data is dropped, but the more delay might exist.
       * in the release phase, if this object constains like 1, 1, we can change them into boolean,
       * but better to keep it for now without testing with the finalized codes.
       * @type {object}
       */
      resourcesCount = {
         audio: 50,
         video: 1
      };
      encodingEnvs = {
         audio: new LimitedSet(resourcesCount.audio),
         video: new LimitedSet(resourcesCount.video)
      };
      encoderStatus = {
         audio: {
            beingUsed: false
         },
         video: {
            beingUsed: false
         }
      };
      enabled = false;
   };

   initInstance = function() {
      try {
         workers = {
            audio: new Worker("./audioworker." + __BUILD_NUMBER__ + ".js"),
            video: new Worker("./videoworker." + __BUILD_NUMBER__ + ".js")
         };
      } catch (e) {
         workers = {
            audio: null,
            video: null
         };
         RTAV.logService.error(e);
      }
      workers.audio.onmessage = handleAudioMessage;
      workers.video.onmessage = handleVideoMessage;

      let handleWorkerError = (e) => {
         RTAV.logService.error(e);
      };
      workers.audio.onerror = handleWorkerError;
      workers.video.onerror = handleWorkerError;
      resetEncoder();
   };

   arrayCopy = function(src, srcPtr, dst, dstPtr, length) {
      let srcArray = new Uint8Array(src, srcPtr, length),
         dstArray = new Uint8Array(dst, dstPtr, length);

      dstArray.set(srcArray);
   };

   setMergedHeader = function() {
      let streamHeaderLength = RTAV.constants.streamHeaderLength,
         videoHeaderLength = headers.video.length + streamHeaderLength,
         audioHeaderLength = headers.audio.length,
         mergedLength = videoHeaderLength + audioHeaderLength,
         mergedHeaderBuffer = new ArrayBuffer(mergedLength);

      arrayCopy(
         headers.video.buffer,
         0,
         mergedHeaderBuffer,
         0,
         videoHeaderLength
      );
      arrayCopy(
         headers.audio.buffer,
         streamHeaderLength,
         mergedHeaderBuffer,
         videoHeaderLength,
         audioHeaderLength
      );

      headers.merged = {
         buffer: mergedHeaderBuffer,
         length: mergedLength - streamHeaderLength
      };
   };

   setAudioHeader = function() {
      let streamHeaderLength = RTAV.constants.streamHeaderLength,
      audioHeaderLength = headers.audio.length + streamHeaderLength ,
      mergedLength = audioHeaderLength,
      mergedHeaderBuffer = new ArrayBuffer(mergedLength);
      arrayCopy(
         headers.audio.buffer,
         0,
         mergedHeaderBuffer,
         0,
         audioHeaderLength
      );
      headers.merged = {
         buffer: mergedHeaderBuffer,
         length: mergedLength - streamHeaderLength
      };
   };

   handleMessage = function(e, dataType) {
      let message = e.data,
         callback,
         env;

      if (!enabled) {
         RTAV.logService.debug(
            "ignore message " +
               message.type +
               " for " +
               dataType +
               " due to encoder disabled"
         );
         return;
      }
      switch (message.type) {
         case "InitDone":
            RTAV.logService.log("media encoder handleMessage initDone enter");
            headers[dataType] = message.data;
            if (typeof callbacks.header !== "function") {
               RTAV.logService.devError("header callback doesn't exist");
               return;
            }
            if (codecPrefs === RTAV.constants.codecTypes['CodecVmwH264Opus']) {
               // in situation 'CodecVmwH264Opus'
               RTAV.logService.log("set merge header");
               callbacks.header(headers.merged);
            } else if (codecPrefs === RTAV.constants.codecTypes['CodecVmwH264Speex']) {
               // in situation 'CodecVmwH264Speex'
               if (headers.video === null && !!headers.audio) {
                  RTAV.logService.log("audio encoder header needed");
                  setAudioHeader();
                  callbacks.header(headers.merged);
               }
            } else {
               // in situation 'CodecVmwTheoraSpeex'
               if (!!headers.video && !!headers.audio) {
                  setMergedHeader();
                  RTAV.logService.log("merged audio and video header");
                  callbacks.header(headers.merged);
               }
            }
            RTAV.logService.log("media encoder handleMessage initDone exit");
            break;
         case "Encoded":
            env = encodingEnvs[dataType].fetch(message.envId);
            if (!env || !env.metaData) {
               RTAV.logService.debug(
                  "negative: bad env read, might caused by switching devices to other desktop"
               );
               return;
            }
            callback = callbacks[dataType];
            if (typeof callback !== "function") {
               RTAV.logService.devError("encoding callback doesn't exist");
               return;
            }
            callback(message.data, env.metaData, env.others);
            resourcesCount[dataType]++;
            break;
         case "Cleared":
            RTAV.logService.log(
               dataType + " encoder cleared:" + message.success
            );
            if (typeof workers[dataType].onCleared === "function") {
               workers[dataType].onCleared(message.success);
               workers[dataType].onCleared = null;
            } else {
               RTAV.logService.devError(
                  dataType + " encoder cleared but no callback found"
               );
            }
            break;
      }
   };

   handleAudioMessage = function(e) {
      handleMessage(e, "audio");
   };

   handleVideoMessage = function(e) {
      handleMessage(e, "video");
   };

   encode = function(sample, type) {
      let dataBuffer, envId;

      if (!workers[type] || !sample || !sample.data) {
         return;
      }
      if (resourcesCount[type] > 0) {
         resourcesCount[type]--;
         dataBuffer = sample.data;
         envId = encodingEnvs[type].insert({
            others: sample.others,
            metaData: sample.timeStamp
         });
         if (envId < 0) {
            RTAV.logService.devError(
               "when encoding media data, environment object can't be saved"
            );
            return;
         }
         workers[type].postMessage(
            {
               type: "Encode",
               data: dataBuffer,
               envId: envId
            },
            [dataBuffer]
         );
      }
   };

   clearWorker = function(type) {
      return new Promise((resolve, reject) => {
         if (workers[type]) {
            RTAV.logService.debug("request clear of worker for " + type);
            workers[type].onCleared = () => {
               RTAV.logService.debug("encoder cleared for " + type);
               resolve();
            };
            workers[type].postMessage({
               type: "Clear"
            });
         } else {
            RTAV.logService.debug(
               "skip clear since no worker found for " + type
            );
            resolve();
         }
      });
   };

   /**
    * This function is to release the resources for encoding.
    */
   clear = function(callback) {
      RTAV.logService.debug(
         "clear encoders for none of audio and video is used"
      );
      Promise.all([clearWorker("audio"), clearWorker("video")]).then(() => {
         RTAV.logService.log("both worker cleared");
         resetEncoder();
         callback(true);
      });
   };

   initInstance();

   return {
      /**
       * Init the encoder using params and register the callbacks to handle header and encoded data
       * now we will provide no default value, so caller must prepare the good init param for initialize
       * @param  {object} audioParam      The param used to init audio encoder
       * @param  {object} videoParam      The param used to init video encoder
       * @param  {function} headerCallback  The callback use to handle inited header
       * @param  {function} audioCallback The callback use to handle encoded audio
       * @param  {function} videoCallback The callback use to handle encoded video
       */
      init: function(
         audioParam,
         videoParam,
         codecPref,
         hardwareAccelerationOption,
         enableRTAVDTX,
         headerCallback,
         audioCallback,
         videoCallback
      ) {
         let videoWorker = workers.video,
            audioWorker = workers.audio;

         if (!videoWorker || !audioWorker || enabled) {
            return;
         }
         callbacks = {
            header: headerCallback,
            audio: audioCallback,
            video: videoCallback
         };
         codecPrefs = codecPref;
         audioParam.logLevel = RTAV.logService.getLevel();
         videoParam.logLevel = audioParam.logLevel;
         videoParam.codecPref = codecPref;
         videoParam.hardwareAccelerationOption = hardwareAccelerationOption;
         audioParam.codecPref = codecPref;
         audioParam.enableRTAVDTX = enableRTAVDTX;
         // although js is non-preemptive, put enabled = true to later place will add complex for UT
         enabled = true;
         audioWorker.postMessage({
            type: "Init",
            data: audioParam
         });
         videoWorker.postMessage({
            type: "Init",
            data: videoParam
         });
      },

      clear: clear,

      /**
       * @param  {object} sample {data: {Int16Array}, timeStamp: {number}}
       */
      encodeAudio: function(sample) {
         encode(sample, "audio");
      },

      /**
       * @param  {object} sample {data: {Uint8Array}, timeStamp: {number}}
       */
      encodeVideo: function(sample) {
         encode(sample, "video");
      },

      /**
       * When we don't need to use the encoder of type, one should call this function to release resources
       * and also set the media encoder to be ready for re-inited. it's related to stop_A and stop_V message
       * @param  {string} type The encoder type name, should be 'audio' or 'video'
       * @returns {Promise} This returns a promise resolved with boolean
       */
      disable: function(type) {
         return new Promise((resolve) => {
            RTAV.logService.debug("disabling enc for " + type);
            if (!encoderStatus[type].beingUsed) {
               RTAV.logService.debug(
                  "negative: no need to disable a disabled encoder: " + type
               );
               resolve(true);
               return;
            }
            encoderStatus[type].beingUsed = false;
            // release all the resources when both encoder are in use no more.
            if (
               !encoderStatus["audio"].beingUsed &&
               !encoderStatus["video"].beingUsed
            ) {
               if (!messageManager) {
                  RTAV.logService.devError(
                     "messageManager don't exist when disable " + type
                  );
                  resolve(false);
                  return;
               }
               if (messageManager.isPermissionPending()) {
                  RTAV.logService.debug(
                     "negative: skip clear encoders since the permission dialog is still pending"
                  );
                  resolve(false);
               } else {
                  RTAV.logService.debug(
                     "clear enc since last request had been disabled " + type
                  );
                  clear(resolve);
               }
            } else {
               RTAV.logService.debug("enc status changed for " + type);
               resolve(true);
            }
         });
      },

      /**
       * When we need to use the encoder of type, one should call this function to record the use status and
       * ready to release resources after both encoder are useded no more. it's related to start_A and start_V
       * message.
       * @param  {string} type The encoder type name, should be 'audio' or 'video'
       * @returns {Promise} This returns a promise resolved with boolean
       */
      enable: function(type) {
         return new Promise((resolve) => {
            if (encoderStatus[type].beingUsed) {
               RTAV.logService.error(
                  "fail to enable a enabled encoder: " + type
               );
               // treat enable a enabled encoder as success
               resolve(true);
               return;
            }
            encoderStatus[type].beingUsed = true;
            resolve(true);
         });
      },

      /**
       * return whether this encoder is inited
       */
      isInited: function() {
         return enabled;
      },

      /**
       * Get previously generated header from the cache, it can faster the whole process and avoid blink in video
       */
      fetchCachedHeader: function() {
         if (!enabled) {
            RTAV.logService.devError(
               "fail to get cached header for enc is not inited yet"
            );
         }
         if (typeof callbacks.header !== "function") {
            RTAV.logService.devError(
               "fail to get cached header for callback is not set yet"
            );
         }
         callbacks.header(headers.merged, true);
      },

      /**
       * Used for Unit Tests to expose some of inner varibles, NEVER use it in the code.
       * @return {obj} This returns the obj contains UT needed data references
       */
      utTestingAPIs: function() {
         return {
            headers: headers,
            callbacks: callbacks
         };
      },

      /**
       * Used for get whether the encoder is working, for bug 1750132.
       * @return {boolean} Whether any of audio or video is being used
       */
      notInUse: function() {
         return (
            !encoderStatus["audio"].beingUsed &&
            !encoderStatus["video"].beingUsed
         );
      },

      /**
       * Used to check the mismatch status before clear up.
       * @param {object} manager A DeviceMessageManager instance
       */
      setMessageManager: function(manager) {
         messageManager = manager;
      }
   };
}